<template>
  <label class="sp-radio-button" :class="classModifiers" :data-testid="testId">
    <input
      :id="id"
      v-model="model"
      :name="name"
      :value="value"
      type="radio"
      class="sp-radio-button__input"
      :aria-checked="isActive"
      :aria-labelledby="labelId"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown.space.enter="handleKeyDown"
    />

    <div class="sp-radio-button__input-icon">
      <sp-icon v-show="isActive" name="check-filled-white"></sp-icon>
    </div>
    <div :id="labelId" class="sp-radio-button__label">
      {{ label }}
    </div>
  </label>
</template>

<script setup>
import { computed, ref } from "vue";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  modelValue: {
    type: String,
  },
  id: {
    type: String,
  },
  name: {
    type: String,
  },
  value: {
    type: String,
  },
  testId: {
    type: String,
    default: undefined,
  },
  label: {
    type: String,
    required: true,
  },
});

const model = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const labelId = computed(() => `label-${props.id}`);
const isFocused = ref(false);

function handleFocus() {
  isFocused.value = true;
}

function handleBlur() {
  isFocused.value = false;
}

function handleKeyDown() {
  model.value = props.value;
}

const isActive = computed(() => model.value === props.value);

const classModifiers = computed(() => ({
  "--active": isActive.value,
  "--focus": isFocused.value,
}));
</script>

<style lang="scss" scoped>
.sp-radio-button {
  --gap: var(--sp-comp-dtv-radio-btn-gap, 1ch);
  --active-color: var(--sp-comp-dtv-radio-btn-active-color, var(--sp-sys-color-primary, #00818a));
  --label-text-default-color: var(--sp-comp-dtv-radio-btn-label-text-color, var(--sp-sys-color-primary, #00818a));
  --label-text-color: var(--label-text-default-color);
  --label-active-text-color: var(--sp-comp-dtv-radio-btn-label-active-text-color, var(--label-text-default-color));
  --button-default-background-color: var(
    --sp-comp-dtv-radio-btn-label-background-color,
    var(--sp-sys-color-surface, #fff)
  );
  --button-background-color: var(--button-default-background-color);
  --button-active-background-color: var(
    --sp-comp-dtv-radio-btn-label-active-background-color,
    var(--button-default-background-color)
  );
  --radio-outline-color: var(--sp-comp-dtv-radio-btn-input-outline-color, var(--sp-sys-form-field-border-color, #aaa));
  --radio-outline-size: var(--sp-comp-dtv-radio-btn-input-outline-size, 2px);
  --radio-input-size: var(--sp-comp-dtv-radio-btn-input-size, var(--sp-sys-icon-size-small, 2rem));
  --radio-input-icon-background-color: var(
    --sp-comp-dtv-radio-btn-input-icon-background-color,
    var(--sp-sys-color-surface, #fff)
  );
  --radio-input-icon-border-color: var(
    --sp-comp-dtv-radio-btn-input-icon-border-color,
    var(--sp-sys-color-outline-variant, #aaa)
  );

  display: grid;
  position: relative;
  width: 100%;
  grid-template-columns: auto 1fr;
  grid-template-areas: "input label";
  gap: var(--gap);
  margin-bottom: 0;
  min-height: var(--sp-comp-dtv-radio-btn-min-height, 3rem);
  cursor: pointer;
  align-items: center;
  padding-inline: var(--sp-comp-dtv-radio-btn-padding-inline, 0);
  padding-block: var(--sp-comp-dtv-radio-btn-padding-block, 0);
  background-color: var(--button-background-color);

  &.--active {
    --radio-input-icon-background-color: var(--active-color);
    --radio-input-icon-border-color: var(--active-color);
    --button-background-color: var(--button-active-background-color);
    --label-text-color: var(--label-active-text-color);
  }
}

.sp-radio-button__input {
  -webkit-appearance: none;
  appearance: none;
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.sp-radio-button__input-icon {
  --icon-size: var(--radio-input-size);
  grid-area: input;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--icon-size);
  height: var(--icon-size);
  background-color: var(--radio-input-icon-background-color);
  border-radius: 50%;
  border: var(--radio-outline-size) solid var(--radio-input-icon-border-color);

  sp-icon {
    --size: var(--icon-size);
    transform: scale(0.75);
  }
}

.sp-radio-button__label {
  display: block;
  font-size: var(--sp-comp-dtv-radio-btn-label-font-size, var(--sp-sys-body-font-size, 0.875rem));
  font-weight: var(--sp-comp-dtv-radio-btn-label-font-weight, 500);
  line-height: var(--sp-comp-dtv-radio-btn-label-line-height, 1.1);
  color: var(--label-text-color);
  padding-block: 0.5rem;
}
</style>
